import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Plus, Trash2, Clock, AlertTriangle, GripVertical } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CustomButtonsSection({ darkMode, customButtons, fetchData }) {
  const [buttonDialogOpen, setButtonDialogOpen] = useState(false);
  const [editingButton, setEditingButton] = useState(null);
  const [buttonForm, setButtonForm] = useState({
    name: '', color: '#6366f1', has_timer: false, has_escalation: false,
    has_dropdown: false, warning_threshold: '', critical_threshold: '', dropdown_options: ''
  });

  const resetButtonForm = () => {
    setButtonForm({ name: '', color: '#6366f1', has_timer: false, has_escalation: false, has_dropdown: false, warning_threshold: '', critical_threshold: '', dropdown_options: '' });
    setEditingButton(null);
  };

  const getPayload = () => ({
    name: buttonForm.name || 'Custom Button',
    color: buttonForm.color,
    has_timer: buttonForm.has_timer,
    has_escalation: buttonForm.has_escalation,
    has_dropdown: buttonForm.has_dropdown,
    warning_threshold: buttonForm.warning_threshold ? parseInt(buttonForm.warning_threshold) * 60 : null,
    critical_threshold: buttonForm.critical_threshold ? parseInt(buttonForm.critical_threshold) * 60 : null,
    dropdown_options: buttonForm.dropdown_options ? buttonForm.dropdown_options.split('\n').filter(o => o.trim()) : []
  });

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/practice/custom-buttons`, getPayload());
      toast.success('Button created');
      setButtonDialogOpen(false);
      resetButtonForm();
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create button'); }
  };

  const handleUpdate = async () => {
    if (!editingButton) return;
    try {
      await axios.put(`${API}/practice/custom-buttons/${editingButton.id}`, getPayload());
      toast.success('Button updated');
      setButtonDialogOpen(false);
      resetButtonForm();
      fetchData();
    } catch { toast.error('Failed to update button'); }
  };

  const handleDelete = async (buttonId) => {
    try {
      await axios.delete(`${API}/practice/custom-buttons/${buttonId}`);
      toast.success('Button deleted');
      fetchData();
    } catch { toast.error('Failed to delete button'); }
  };

  const handleToggle = async (button) => {
    try {
      await axios.put(`${API}/practice/custom-buttons/${button.id}`, { enabled: !button.enabled });
      fetchData();
    } catch { toast.error('Failed to update button'); }
  };

  const openEdit = (button) => {
    setEditingButton(button);
    setButtonForm({
      name: button.name,
      color: button.color || '#6366f1',
      has_timer: button.has_timer || false,
      has_escalation: button.has_escalation || false,
      has_dropdown: button.has_dropdown || false,
      warning_threshold: button.warning_threshold ? Math.floor(button.warning_threshold / 60).toString() : '',
      critical_threshold: button.critical_threshold ? Math.floor(button.critical_threshold / 60).toString() : '',
      dropdown_options: (button.dropdown_options || []).join('\n')
    });
    setButtonDialogOpen(true);
  };

  return (
    <div className={`glass-panel rounded-2xl p-6 ${darkMode ? 'bg-slate-800/50' : ''}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className={`text-xl font-heading font-bold flex items-center gap-2 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
            <Plus className="w-5 h-5 text-indigo-600" />
            Custom Buttons
          </h2>
          <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Create up to 6 custom buttons that appear in a floating island at the bottom of the dashboard
          </p>
        </div>
        
        <Dialog open={buttonDialogOpen} onOpenChange={(open) => { setButtonDialogOpen(open); if (!open) resetButtonForm(); }}>
          <DialogTrigger asChild>
            <Button disabled={customButtons.length >= 6} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="add-button-btn">
              <Plus className="w-4 h-4 mr-2" />Add Button
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto dark-scrollbar" style={darkMode ? { backgroundColor: '#1e293b', borderColor: '#334155' } : {}}>
            <DialogHeader>
              <DialogTitle className={`font-heading text-2xl ${darkMode ? 'text-slate-100' : ''}`}>
                {editingButton ? 'Edit Custom Button' : 'Create Custom Button'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <Label style={darkMode ? { color: '#e2e8f0' } : {}}>Button Name</Label>
                <Input value={buttonForm.name} onChange={(e) => setButtonForm({...buttonForm, name: e.target.value})} placeholder="e.g., Lab Ready, Running Behind" className="mt-1" style={darkMode ? { backgroundColor: '#334155', borderColor: '#475569', color: '#f1f5f9' } : {}} />
              </div>
              
              <div>
                <Label style={darkMode ? { color: '#e2e8f0' } : {}}>Button Color</Label>
                <div className="flex items-center gap-3 mt-1">
                  <input type="color" value={buttonForm.color} onChange={(e) => setButtonForm({...buttonForm, color: e.target.value})} className="w-12 h-10 rounded-lg cursor-pointer border-2" style={darkMode ? { borderColor: '#475569', backgroundColor: '#334155' } : { borderColor: '#e2e8f0' }} />
                  <div className="flex-1 h-10 rounded-lg flex items-center justify-center text-white font-medium" style={{ backgroundColor: buttonForm.color }}>
                    {buttonForm.name || 'Preview'}
                  </div>
                </div>
              </div>
              
              <div className="space-y-3 pt-2 border-t" style={darkMode ? { borderColor: '#475569' } : { borderColor: '#e2e8f0' }}>
                <Label className="text-sm font-semibold" style={darkMode ? { color: '#e2e8f0' } : { color: '#334155' }}>Button Features</Label>
                
                <ToggleRow label="Count-up Timer" icon={<Clock className="w-4 h-4" />} checked={buttonForm.has_timer} onChange={() => setButtonForm({...buttonForm, has_timer: !buttonForm.has_timer})} darkMode={darkMode} />
                <ToggleRow label="Escalation Marker" icon={<AlertTriangle className="w-4 h-4" />} checked={buttonForm.has_escalation} onChange={() => setButtonForm({...buttonForm, has_escalation: !buttonForm.has_escalation})} darkMode={darkMode} />
                <ToggleRow label="Dropdown with Custom Text" icon={<GripVertical className="w-4 h-4" />} checked={buttonForm.has_dropdown} onChange={() => setButtonForm({...buttonForm, has_dropdown: !buttonForm.has_dropdown})} darkMode={darkMode} />
              </div>
              
              {buttonForm.has_escalation && (
                <div className="space-y-3 p-3 rounded-lg" style={darkMode ? { backgroundColor: '#334155' } : { backgroundColor: '#f8fafc' }}>
                  <Label style={darkMode ? { color: '#e2e8f0' } : {}}>Escalation Thresholds</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs" style={darkMode ? { color: '#94a3b8' } : { color: '#64748b' }}>Warning (minutes)</Label>
                      <Input type="number" value={buttonForm.warning_threshold} onChange={(e) => setButtonForm({...buttonForm, warning_threshold: e.target.value})} placeholder="5" className="mt-1" style={darkMode ? { backgroundColor: '#1e293b', borderColor: '#475569', color: '#f1f5f9' } : {}} />
                    </div>
                    <div>
                      <Label className="text-xs" style={darkMode ? { color: '#94a3b8' } : { color: '#64748b' }}>Critical (minutes)</Label>
                      <Input type="number" value={buttonForm.critical_threshold} onChange={(e) => setButtonForm({...buttonForm, critical_threshold: e.target.value})} placeholder="7" className="mt-1" style={darkMode ? { backgroundColor: '#1e293b', borderColor: '#475569', color: '#f1f5f9' } : {}} />
                    </div>
                  </div>
                </div>
              )}
              
              {buttonForm.has_dropdown && (
                <div>
                  <Label style={darkMode ? { color: '#e2e8f0' } : {}}>Dropdown Options (one per line)</Label>
                  <textarea value={buttonForm.dropdown_options} onChange={(e) => setButtonForm({...buttonForm, dropdown_options: e.target.value})} placeholder={"Option 1\nOption 2\nOption 3"} className="w-full mt-1 p-2 border rounded-lg text-sm min-h-[80px] focus:outline-none focus:ring-2 focus:ring-indigo-500" style={darkMode ? { backgroundColor: '#334155', borderColor: '#475569', color: '#f1f5f9' } : {}} />
                </div>
              )}
              
              <Button onClick={editingButton ? handleUpdate : handleCreate} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white">
                {editingButton ? 'Save Changes' : 'Create Button'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Custom Buttons List */}
      {customButtons.length === 0 ? (
        <div className={`text-center py-8 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          <Plus className={`w-12 h-12 mx-auto mb-3 ${darkMode ? 'text-slate-600' : 'text-slate-300'}`} />
          <p>No custom buttons yet</p>
          <p className="text-sm mt-1">Create buttons to add quick actions to the dashboard</p>
        </div>
      ) : (
        <div className="space-y-3">
          {customButtons.map(button => (
            <div key={button.id} className={`flex items-center justify-between p-4 rounded-xl border ${darkMode ? 'border-slate-600 bg-slate-700/50' : 'border-slate-200 bg-white/50'}`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: button.color || '#6366f1' }}>
                  {(button.name || '?').charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className={`font-medium ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>{button.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {button.has_timer && <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-slate-600 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>Timer</span>}
                    {button.has_escalation && <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>Escalation</span>}
                    {button.has_dropdown && <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>Dropdown</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => handleToggle(button)} className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full transition-colors ${button.enabled !== false ? 'bg-emerald-500' : (darkMode ? 'bg-slate-600' : 'bg-slate-300')}`}>
                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition-transform mt-0.5 ${button.enabled !== false ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </button>
                <Button variant="ghost" size="sm" onClick={() => openEdit(button)} className={darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-800'}>Edit</Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(button.id)} className={`text-red-500 ${darkMode ? 'hover:text-red-400' : 'hover:text-red-700'}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ToggleRow({ label, icon, checked, onChange, darkMode }) {
  return (
    <div className="flex items-center gap-3">
      <button onClick={onChange} className="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full transition-colors" style={{ backgroundColor: checked ? '#6366f1' : (darkMode ? '#475569' : '#cbd5e1') }}>
        <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition-transform mt-0.5 ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </button>
      <div className="flex items-center gap-2">
        <span style={{ color: darkMode ? '#94a3b8' : '#64748b' }}>{icon}</span>
        <span className="text-sm" style={{ color: darkMode ? '#cbd5e1' : '#334155' }}>{label}</span>
      </div>
    </div>
  );
}
